import { useEffect, useRef } from 'react';
import styled from 'styled-components';

const StyledVideo = styled.video`
  max-width: 100%;
  max-height: 70vh;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  background: #000;
`;

type Props = {
  src: string;
  poster?: string;
  className?: string;
  loop?: boolean;
  muted?: boolean; // default true
};

const AutoPlayVideo = ({
  src,
  poster,
  className,
  loop = true,
  muted = true,
}: Props) => {
  const ref = useRef<HTMLVideoElement | null>(null);
  const hasSrcRef = useRef(false);

  const pauseOthers = (self: HTMLVideoElement) => {
    const nodes = document.querySelectorAll<HTMLVideoElement>(
      'video[data-autoplay]',
    );
    nodes.forEach((v) => {
      if (v !== self && !v.paused) v.pause();
    });
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // iOS/Safari friendliness
    el.muted = muted ?? true;
    el.playsInline = true; // programmatic set in addition to attribute

    const tryPlay = () => {
      if (!el) return;
      // If media is not ready yet, wait for canplay then retry
      const doPlay = () =>
        el.play().catch(() => {
          /* ignore blocked play */
        });

      if (el.readyState >= 2) {
        pauseOthers(el);
        doPlay();
      } else {
        const onCanPlay = () => {
          el.removeEventListener('canplay', onCanPlay);
          pauseOthers(el);
          doPlay();
        };
        el.addEventListener('canplay', onCanPlay);
      }
    };

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry || !el) return;

        // 1) Lazy-load early, before entering viewport
        if (
          !hasSrcRef.current &&
          (entry.isIntersecting || entry.intersectionRatio > 0)
        ) {
          // set src only once, then force the browser to begin fetching
          el.src = src;
          el.load();
          hasSrcRef.current = true;
        }

        // 2) Autoplay/pause when ~60% visible
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          tryPlay();
        } else {
          el.pause();
        }
      },
      {
        // Start loading ~400px before it appears; tweak as needed
        rootMargin: '400px 0px',
        threshold: [0, 0.25, 0.6, 1],
      },
    );

    io.observe(el);

    // Pause others if user manually hits play
    const onUserPlay = () => pauseOthers(el);
    el.addEventListener('play', onUserPlay);

    // Pause when tab hidden
    const onVisibility = () => {
      if (document.hidden) el.pause();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      io.disconnect();
      el.removeEventListener('play', onUserPlay);
      document.removeEventListener('visibilitychange', onVisibility);
      el.pause();
    };
  }, [src, muted]);

  return (
    <StyledVideo
      ref={ref}
      poster={poster}
      // keep attributes too (some browsers check attributes before JS runs)
      muted={muted}
      playsInline
      loop={loop}
      controls
      preload="none" // don't fetch until we assign src + load()
      data-autoplay
      className={className}
    />
  );
};

export default AutoPlayVideo;
